import React, { useCallback } from 'react'
import { Category2, SecurityUser, Setting2, Teacher, Video } from 'iconsax-react'
import { Link, useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { ChevronDown } from 'components/lib/DesignSystem/Icons'

import { navRowHover, navSectionActive } from '../navStyles'

import EmailEncryptionIcon from './icons/EmailEncryptionIcon'
import LockedNavItem from './LockedNavItem'
import NavPage from './NavPage'

// First page a collapsed section-icon click should land on. Skip disabled
// rows: on mobile every page except Incidents is inert (IC-49684 #2).
export const firstNavigableChildPath = (children) =>
  (Array.isArray(children) ? children : []).find(
    (c) => c && c.visible && !c.locked && !c.comingSoon && !c.disabled && c.resolvedPath
  )?.resolvedPath || null

// ── Icon map ─────────────────────────────────────────────────────────────────

// Every section icon swaps Outline -> Bold when the section is active. iconsax-react
// ships the vuesax glyphs (Figma file ljWfuNCiTDB4nbdczYwdV0, node 1:26644
// "Open nav -max features OPEN") with a `variant` prop. Account Take Over maps to
// SecurityUser: its shield outer path is coordinate-identical to the design's
// `vuesax/outline/frame`, and — unlike the old fixed-path DS `Frame` asset, which
// had no bold companion and so only recolored — it carries a Bold variant. That was
// the icon selected-state inconsistency: ATO stayed outline while every other active
// section filled + went blue. It now swaps like the rest.
//
// Mailbox (Email Security) is not iconsax `Sms` — that glyph is an envelope. The
// section uses the DS 3.0 shield-with-padlock (IC-52560). Encryption keeps the same
// asset (IC-51144): one glyph, two sections, distinguished by their labels. See
// ./icons/EmailEncryptionIcon.js — it mirrors iconsax's Icon prop contract so it
// drops in here.
const ICONSAX_ICON_MAP = {
  Dashboard: Category2,
  Mailbox: EmailEncryptionIcon,
  Deepfake: Video,
  Encryption: EmailEncryptionIcon,
  Awareness: Teacher,
  CompanySettings: Setting2,
  FillAto: SecurityUser,
}

// ── Styled components ─────────────────────────────────────────────────────────

const BellDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary);
  flex-shrink: 0;
`

// L1 section row — Figma node 1:16633 (Navigation, "Close nav -Max features").
// Icon (24px) + Poppins SemiBold 14px label + right chevron; height 40px, px 9px,
// radius 10px. The 40px row height (was 36) is what opens up the gap to the first
// inner page to match the design; 9px px lands the label directly above the L2
// text indent. Active section uses the selected-background token with body text.
const RowBase = css`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 9px;
  border-radius: 10px;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  /* Text hierarchy (design review 2026-08): every nav item rests at Medium (500);
     the active trail reads Bold (700) — see the $active block below. */
  font-weight: 500;
  line-height: 20px;
  color: var(--color-text);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  width: 100%;
  box-sizing: border-box;
  border: none;
  background: transparent;
  text-align: left;

  ${navRowHover}

  /* Section-active background pill is an expanded-row-only affordance (Figma: collapsed +
     active has no pill/circle behind the icon — just the icon itself recoloring, see below). */
  ${({ $active, $collapsed }) => $active && !$collapsed && navSectionActive}

  /* Active trail is Bold (design review 2026-08): the current page AND its parent
     L1 section both bold, so an expanded active section header reads Bold too —
     whether it's a folder (contains the active page) or a direct page. */
  ${({ $active, $collapsed }) =>
    $active &&
    !$collapsed &&
    css`
      font-weight: 700;
    `}

  /* Locked L1 rows are the lightest grey in the rail (Figma rail-states: #CCCDD5 at
     L1, #AAABB8 at L2 — NavPage.js owns the L2 value). --sub-text-color (#565b6d)
     was far too dark to read as disabled. */
  ${({ $locked, $collapsed }) =>
    $locked &&
    css`
      color: var(--t-colors-gray-300);

      ${
        $collapsed &&
        css`
          /* Figma 1:19324 — hovering a collapsed locked item warms the whole glyph
           (icon + padlock) to the upsell amber, with NO hover pill behind it; the
           LockedNavItem tooltip + gold padlock carry the rest of the affordance. */
          &:hover,
          &:focus:hover {
            color: var(--warning-color);
            background-color: transparent;
          }
        `
      }
    `}

  /* Collapsed icon button — Figma node 1:17402 "Container": 42×40, centered in
     the 60px rail's 9px insets. */
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      width: 42px;
      padding: 0;
      height: 40px;
      border-radius: 12px;
      margin: 0 auto;
    `}

  /* Collapsed + active section: besides the icon recoloring, mark the selected
     section with a blue bar hugging the rail's left edge (Figma "closed nav /
     selected"). The button sits 9px in from the rail edge (ScrollArea inset), so
     pull the bar back by that 9px to sit flush against the rail border. Expanded
     rows already carry their own bar via navRowActive on the child page. */
  ${({ $active, $collapsed }) =>
    $active &&
    $collapsed &&
    css`
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: -9px;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 24px;
        border-radius: 0 4px 4px 0;
        background: var(--primary);
      }

      /* Selected collapsed icon glow (Figma "closed nav / selected"): the active
         glyph is a filled blue square (Bold variant); a soft primary-blue halo
         lifts it off the dark rail. drop-shadow follows the glyph's rounded-square
         alpha rather than boxing the whole 42px hit target. Dark theme only — on
         the light rail the glow reads as an unwanted smudge (design review). */
      html[data-theme='dark'] & svg {
        filter: drop-shadow(0 0 6px rgba(48, 99, 253, 0.6))
          drop-shadow(0 0 14px rgba(48, 99, 253, 0.35));
      }
    `}
`

const SectionButton = styled.button`
  ${RowBase}
`

// styled-components v4 does NOT strip `$`-prefixed transient props before
// forwarding to a custom component, so styled(Link) would leak `$active`/`$collapsed`
// onto the DOM <a>. Strip them here before they reach react-router's Link.
const FilteredLink = ({ $active, $collapsed, $locked, ...rest }) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Link {...rest} />
)

const SectionLink = styled(FilteredLink)`
  ${RowBase}
`

const SectionExternalLink = styled.a`
  ${RowBase}
`

// Active-section icon (expanded AND collapsed): Figma 1:26685 renders the glyph in
// var(--primary) while the label (when visible) stays navy (var(--color-text) via RowBase).
// The glyph also swaps Outline->Bold via the iconsax `variant` prop passed at the call site.
const IconWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: ${({ $activeIcon }) => ($activeIcon ? 'var(--primary)' : 'currentColor')};

  svg,
  svg path:not([fill='none']) {
    fill: currentColor;
  }
`

const LabelWrapper = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ChevronWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg,
  svg path {
    fill: currentColor;
  }
`

const BellWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`

// Collapsed rail is icon-only. The active glyph is filled primary-blue, the same
// color as the dot, so the badge has to sit in the padding beside the icon
// (the row is 42×40 around a 24px glyph) or it vanishes into the icon.
const CollapsedBell = styled.span`
  position: absolute;
  top: -2px;
  right: -7px;
  z-index: 1;
  display: inline-flex;
  pointer-events: none;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--color-elevated-solid);
`

// L2 sub-pages are inset as a GROUP (Figma 1:26644, node 1:26674: the child
// "Container" sits at x=31 from the section's left edge, so every L2 row/pill —
// hover wash and active bar included — steps in under the section instead of
// spanning the full rail width like the L1 row. NavPage then only pads the text
// 12px inside this inset (its own Link px), landing it under the L1 label.
const ChildrenList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 0 31px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ChildItem = styled.li``

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * NavSection — L1 row.
 *
 * Behaviour:
 * - If the node has children → button that expands/collapses the child list (does NOT navigate).
 * - If the node is a direct-page section (no children, has path) → Link that navigates.
 * - Active highlight when active===true.
 * - Non-numbered bell dot when hasBellNotification (AC5).
 * - Locked wrapping via LockedNavItem (AC4).
 * - Collapsed mode: hide label + chevron; flyout is handled by NavFlyout in SideNav.
 *
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {string} [props.icon] - icon name (key in ICONSAX_ICON_MAP or ICON_MAP)
 * @param {string} [props.resolvedPath] - only set for direct-page sections
 * @param {boolean} [props.active]
 * @param {boolean} [props.locked]
 * @param {string} [props.plgPath]
 * @param {boolean} [props.hasBellNotification]
 * @param {boolean} [props.external]
 * @param {boolean} [props.newTab]
 * @param {boolean} props.collapsed - panel collapsed state
 * @param {boolean} [props.open] - child list expanded state (for section-with-children)
 * @param {(id: string) => void} [props.onToggleExpand] - called when section button is clicked
 * @param {Array} [props.children] - decorated child NavNodes
 * @param {string} [props.className]
 */
const NavSection = ({
  id,
  label,
  icon,
  resolvedPath,
  active,
  locked,
  plgPath,
  hasBellNotification,
  external,
  newTab,
  collapsed,
  open,
  onToggleExpand,
  children,
  className,
}) => {
  const history = useHistory()
  const hasChildren = Array.isArray(children) && children.length > 0
  const IconsaxComponent = icon ? ICONSAX_ICON_MAP[icon] : null

  // Pendo Feature tags (ui/.ai/skills/pendo-feature-marking.md): static, kebab,
  // one value app-wide. Section/page ids are stable nav-config slugs (navConfig.js:
  // `email-security`, `account-take-over`, …), so interpolating them is safe — same
  // pattern as the app's `reports-tab-${key}` tags. Locked rows tag the upsell click
  // on the LockedNavItem wrapper (the real PLG click target), not the inert row, so
  // adoption of an entitled section and an upsell click never share a Feature. Theme
  // (light/dark) is NOT baked in here — it's already a Pendo visitor property
  // (dark_mode_enabled, utils/pendo.js), so segment by it instead of doubling tags.
  const rowPendoFeature = locked ? undefined : `nav-l1-${id}`
  const lockedPendoFeature = `nav-upsell-${id}`

  const firstChildPath = firstNavigableChildPath(children)

  const handleToggle = useCallback(() => {
    if (locked) return
    // Collapsed: the child list can't render inline, so an expand-toggle is a
    // dead click (every top section is a folder — nothing would navigate).
    // Route straight to the section's first page instead; the hover flyout still
    // exposes the full child list for picking a different one.
    if (collapsed && firstChildPath) {
      history.push(firstChildPath)
      return
    }
    if (onToggleExpand) onToggleExpand(id)
  }, [id, locked, collapsed, firstChildPath, history, onToggleExpand])

  const bellEl =
    hasBellNotification && !collapsed ? (
      <BellWrapper aria-label="Has notifications">
        <BellDot aria-hidden="true" />
      </BellWrapper>
    ) : null

  const collapsedBell =
    hasBellNotification && collapsed ? (
      <CollapsedBell aria-label="Has notifications">
        <BellDot aria-hidden="true" />
      </CollapsedBell>
    ) : null

  const iconEl = IconsaxComponent ? (
    <IconWrapper $activeIcon={active}>
      <IconsaxComponent variant={active ? 'Bold' : 'Outline'} size={24} color="currentColor" />
      {collapsedBell}
    </IconWrapper>
  ) : null

  // Build the row element
  let rowEl

  if (hasChildren) {
    // Section with children → button (expand/collapse, no navigate)
    rowEl = (
      <SectionButton
        id={`nav-section-${id}`}
        type="button"
        data-pendo-feature={rowPendoFeature}
        $active={active}
        $collapsed={collapsed}
        $locked={locked}
        onClick={handleToggle}
        aria-label={collapsed ? label : undefined}
        aria-expanded={collapsed || locked ? undefined : open}
        aria-controls={collapsed || locked ? undefined : `nav-section-children-${id}`}
        className={className}
      >
        {iconEl}
        {!collapsed && <LabelWrapper>{label}</LabelWrapper>}
        {!collapsed && bellEl}
        {!collapsed && !locked && (
          <ChevronWrapper $open={open} aria-hidden="true">
            <ChevronDown noDefaultFill fill="currentColor" width="16px" height="16px" />
          </ChevronWrapper>
        )}
      </SectionButton>
    )
  } else if (resolvedPath) {
    // Direct-page section → Link (internal) or anchor (external)
    if (external) {
      rowEl = (
        <SectionExternalLink
          id={`nav-section-${id}`}
          href={resolvedPath}
          target={newTab ? '_blank' : '_self'}
          rel="noopener noreferrer"
          data-pendo-feature={rowPendoFeature}
          $active={active}
          $collapsed={collapsed}
          $locked={locked}
          aria-label={collapsed ? label : undefined}
          aria-current={active ? 'page' : undefined}
          className={className}
        >
          {iconEl}
          {!collapsed && <LabelWrapper>{label}</LabelWrapper>}
          {!collapsed && bellEl}
        </SectionExternalLink>
      )
    } else {
      rowEl = (
        <SectionLink
          id={`nav-section-${id}`}
          to={resolvedPath}
          target={newTab ? '_blank' : '_self'}
          data-pendo-feature={rowPendoFeature}
          $active={active}
          $collapsed={collapsed}
          $locked={locked}
          aria-label={collapsed ? label : undefined}
          aria-current={active ? 'page' : undefined}
          className={className}
        >
          {iconEl}
          {!collapsed && <LabelWrapper>{label}</LabelWrapper>}
          {!collapsed && bellEl}
        </SectionLink>
      )
    }
  } else {
    // Fallback: inert button (shouldn't normally occur)
    rowEl = (
      <SectionButton
        id={`nav-section-${id}`}
        type="button"
        data-pendo-feature={rowPendoFeature}
        $active={active}
        $collapsed={collapsed}
        $locked={locked}
        aria-label={collapsed ? label : undefined}
        className={className}
      >
        {iconEl}
        {!collapsed && <LabelWrapper>{label}</LabelWrapper>}
        {!collapsed && bellEl}
      </SectionButton>
    )
  }

  // Wrap in LockedNavItem if locked
  const wrappedRow = locked ? (
    <LockedNavItem
      label={label}
      plgPath={plgPath}
      collapsed={collapsed}
      dataPendoFeature={lockedPendoFeature}
    >
      {rowEl}
    </LockedNavItem>
  ) : (
    rowEl
  )

  // Locked sections render the row only. The click goes to the upgrade page.
  if (!hasChildren || collapsed || locked) {
    return wrappedRow
  }

  // Render section + expandable child list
  return (
    <SectionWrapper>
      {wrappedRow}
      {open && (
        <ChildrenList id={`nav-section-children-${id}`} role="list">
          {children
            .filter((child) => child.visible)
            .map((child) => (
              <ChildItem key={child.id} role="listitem">
                <NavPage
                  id={`nav-page-${child.id}`}
                  dataPendoFeature={
                    child.locked ? `nav-upsell-page-${child.id}` : `nav-page-${child.id}`
                  }
                  label={child.label}
                  resolvedPath={child.resolvedPath}
                  active={child.active}
                  disabled={child.disabled}
                  locked={child.locked}
                  plgPath={child.plg?.path}
                  hasBellNotification={child.hasBellNotification}
                  newTab={child.newTab}
                  external={child.external}
                  comingSoon={child.comingSoon}
                  tag={child.tag}
                  depth={1}
                />
              </ChildItem>
            ))}
        </ChildrenList>
      )}
    </SectionWrapper>
  )
}

export default NavSection
